<template>
  <b-modal
    id="SaveTestFixtureModal"
    size="lg"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @hidden="doCancel"
  >
    <template
      slot="modal-header"
      slot-scope="{ close }"
    >
      <h5>{{ modalTitle }}</h5>
      <button
        class="btn btn--close"
        @click="close()"
      >
        <font-awesome-icon
          :icon="['fal', 'times']"
          class="icon alt"
        />
      </button>
    </template>

    <form-component>
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <b-card no-body>
          <b-tabs card>
            <b-tab
              title="System"
              active
            >
              <b-card-text>
                <input-text
                  id="serialNumber"
                  v-model="serialNumber"
                  v-validate="'required'"
                  name="serialNumber"
                  data-vv-name="serialNumber"
                  data-vv-as="Device Serial"
                  label="Device Serial"
                  type="text"
                  required
                  :disabled="id > 0"
                  :error="errors.first('serialNumber')"
                />

                <input-select
                  v-if="$store.getters.isKgbAdminUser"
                  id="organization"
                  v-model="organization"
                  v-validate="'required'"
                  name="organization"
                  data-vv-name="organization"
                  data-vv-as="Organization"
                  label="Organization"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('organization')"
                  :options="activeOrganizations"
                  key-field="id"
                  display-field="name"
                  @change="changedOrganization"
                />

                <input-select
                  v-if="$store.getters.isAdminUser && sites"
                  id="site"
                  v-model="site"
                  name="site"
                  data-vv-name="site"
                  data-vv-as="Site"
                  label="Site"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :options="sites"
                  key-field="id"
                  display-field="name"
                />

                <input-select
                  id="disabled"
                  v-model="disabled"
                  v-validate="'required'"
                  name="disabled"
                  data-vv-name="disabled"
                  data-vv-as="Status"
                  label="Status"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('disabled')"
                  :options="negitiveStatusOptions"
                />

                <input-select
                  id="deviceType"
                  v-model="deviceType"
                  v-validate="'required'"
                  name="deviceType"
                  data-vv-name="deviceType"
                  data-vv-as="Device Type"
                  label="Device Type"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('deviceType')"
                  :options="deviceTypeOptions"
                />

                <input-select
                  id="accessLevel"
                  v-model="accessLevel"
                  v-validate="'required'"
                  name="accessLevel"
                  data-vv-name="accessLevel"
                  data-vv-as="Access Level"
                  label="Access Level"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('accessLevel')"
                  :options="accessLevelOptions"
                />
              </b-card-text>
            </b-tab>

            <b-tab title="Connectivity">
              <b-card-text>
                <input-select
                  id="allowWifi"
                  v-model="allowWifi"
                  v-validate="'required'"
                  name="allowWifi"
                  data-vv-name="allowWifi"
                  data-vv-as="WiFi"
                  label="WiFi"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowWifi')"
                  :options="statusOptions"
                />
                <input-select
                  id="allowEthernet"
                  v-model="allowEthernet"
                  v-validate="'required'"
                  name="allowEthernet"
                  data-vv-name="allowEthernet"
                  data-vv-as="Ethernet"
                  label="Ethernet"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowEthernet')"
                  :options="statusOptions"
                />
                <input-select
                  id="allowBluetooth"
                  v-model="allowBluetooth"
                  v-validate="'required'"
                  name="allowBluetooth"
                  data-vv-name="allowBluetooth"
                  data-vv-as="Bluetooth"
                  label="Bluetooth"
                  :disabled="!$store.getters.isKgbAdminUser"
                  :required="true"
                  :error="errors.first('allowBluetooth')"
                  :options="statusOptions"
                />
              </b-card-text>
            </b-tab>
          </b-tabs>
        </b-card>
      </form>
    </form-component>

    <template
      slot="modal-footer"
    >
      <button
        class="btn"
        type="button"
        @click="doCancel()"
      >
        Cancel
      </button>
      <button
        v-if="$store.getters.isKgbAdminUser"
        class="btn btn--primary"
        type="button"
        :disabled="saving"
        @click="saveDevice()"
      >
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Save</span>
      </button>
    </template>
  </b-modal>
</template>

<script>
/* eslint-disable vue/require-default-prop */
import FormComponent from '@/components/FormComponent.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';

const defaultSites = [{ id: '-1', name: 'Primary' }];

export default {
  name: 'SaveTestFixtureModal',
  components: {
    FormComponent,
    InputText,
    InputSelect,
  },
  props: {
    doCancel: {
      type: Function,
    },
    organizations: {
      type: Array,
    },
    doSaveTestFixture: {
      type: Function,
    },
    getOrganizationSites: {
      type: Function,
    },
  },
  data() {
    return {
      id: 0,
      serialNumber: '',
      organization: '',
      sites: [],
      site: '-1',
      disabled: '',
      deviceType: '',
      accessLevel: '',
      allowWifi: '',
      allowEthernet: '',
      allowBluetooth: '',

      statusOptions: [
        { id: 'true', display_label: 'Enabled' },
        { id: 'false', display_label: 'Disabled' },
      ],
      negitiveStatusOptions: [
        { id: 'false', display_label: 'Enabled' },
        { id: 'true', display_label: 'Disabled' },
      ],
      offloadOptions: [
        { id: 'none', display_label: 'None' },
        { id: 'all', display_label: 'All' },
        { id: 'data', display_label: 'DataOnly' },
        { id: 'log', display_label: 'LogOnly' },
      ],
      deviceTypeOptions: [
        { id: 'cableTest', display_label: 'Cable Test Fixture' },
      ],
      accessLevelOptions: [
        { id: 'admin', display_label: 'Admin' },
        { id: 'distributor', display_label: 'Distributor' },
      ],
      saving: false,
    };
  },
  computed: {
    modalTitle() {
      if (this.id === 0) {
        return 'New Test Fixture';
      }
      if (!this.$store.getters.isKgbAdminUser) {
        return `Test Fixture ${this.serialNumber}`;
      }
      return `Edit Test Fixture ${this.serialNumber}`;
    },
    activeOrganizations() {
      return this.organizations.filter(x => !x.is_removed);
    },
  },
  mounted() {

  },
  methods: {
    saveDevice() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.errors.clear();
          this.saving = true;
          this.doSaveTestFixture(this.id, {
            serialNumber: this.serialNumber,
            organization: this.organization,
            site: this.site === '-1' ? null : this.site,
            disabled: this.disabled,
            deviceType: this.deviceType,
            accessLevel: this.accessLevel,
            allowWifi: this.allowWifi,
            allowEthernet: this.allowEthernet,
            allowBluetooth: this.allowBluetooth,
          }, () => {
            this.saving = false;
          });
        }
      });
    },
    populateSites() {
      this.sites = null;
      this.getOrganizationSites(this.organization)
        .then((sites) => { this.sites = [...defaultSites, ...sites]; });
    },
    changedOrganization() {
      this.encryptionKeys = [];
      this.site = '-1';
      this.populateSites();
    },
    setDeviceModal(model) {
      this.saving = false;
      this.id = model.id || 0;
      this.serialNumber = model.serialNumber || '';
      this.organization = `${model.organization || ''}`;
      this.site = `${model.site || '-1'}`;
      this.disabled = 'false';
      if (this.id && model.disabled) {
        this.disabled = 'true';
      }
      if (this.organization) {
        this.populateSites();
      }
      this.deviceType = model.deviceType || 'cableTest';
      this.accessLevel = model.accessLevel || '';
      this.allowWifi = (this.id === 0) || model.allowWifi ? 'true' : 'false';
      this.allowEthernet = (this.id === 0) || model.allowEthernet ? 'true' : 'false';
      this.allowBluetooth = (this.id === 0) || model.allowBluetooth ? 'true' : 'false';
    },
  },
};
</script>

<style scoped lang="scss">

</style>
